// Session state for the whole app. The backend owns the session (HttpOnly cookie); this context only
// mirrors /auth/status so routes and navigation can adapt to the signed-in role.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { setCsrfToken } from '../../lib/api';
import { auth } from '../../lib/trade';
import type { AuthStatus, Me, Portal } from '../../types/trade';

export type ModuleMode = 'admin' | 'buyer' | 'seller';

interface AuthContextValue {
  user: Me | null;
  status: AuthStatus | undefined;
  loading: boolean;
  /** Plant Decision Engine enabled by server (ENGINE_ENABLED) or client environment. */
  engineEnabled: boolean;
  /** Engine pages are open to anonymous visitors in dev (ENGINE_ACCESS=open), staff-only otherwise. False if engine is disabled. */
  engineAllowed: boolean;
  /** Active UI perspective: 'admin', 'buyer', or 'seller'. */
  activeModule: ModuleMode;
  /** Allows super admin to switch between Admin, Buyer, and Seller module views. */
  setActiveModule: (mode: ModuleMode) => void;
  /** Whether the signed-in account can switch module views (true for super_admin). */
  canSwitchModule: boolean;
  login: (email: string, password: string, portal: Portal) => Promise<Me>;
  logout: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['auth-status'], queryFn: ({ signal }) => auth.status(signal), staleTime: 60_000, retry: 1 });
  const status = query.data;
  const user = status?.user ?? null;

  const [overrideModule, setOverrideModule] = useState<ModuleMode | null>(() => {
    return (sessionStorage.getItem('active_portal_module') as ModuleMode) || null;
  });

  const canSwitchModule = user?.role === 'super_admin';

  const activeModule: ModuleMode = useMemo(() => {
    if (!user) return 'admin';
    if (user.role === 'buyer') return 'buyer';
    if (user.role === 'seller') return 'seller';
    if (user.role === 'super_admin' && overrideModule) return overrideModule;
    return 'admin';
  }, [user, overrideModule]);

  const setActiveModule = useCallback((mode: ModuleMode) => {
    setOverrideModule(mode);
    sessionStorage.setItem('active_portal_module', mode);
  }, []);

  const login = useCallback(async (email: string, password: string, portal: Portal) => {
    const me = await auth.login({ email, password, portal });
    if (me.csrf_token) setCsrfToken(me.csrf_token);
    if (portal === 'buyer') {
      setOverrideModule('buyer');
      sessionStorage.setItem('active_portal_module', 'buyer');
    } else if (portal === 'seller') {
      setOverrideModule('seller');
      sessionStorage.setItem('active_portal_module', 'seller');
    } else {
      setOverrideModule('admin');
      sessionStorage.setItem('active_portal_module', 'admin');
    }
    // A new identity: drop everything cached for the previous one. The session query itself is
    // updated in place (removing it would detach the provider's observer and hide the new user).
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth-status' });
    qc.setQueryData<AuthStatus>(['auth-status'], (prev) => ({ engine_enabled: prev?.engine_enabled, engine_access: prev?.engine_access ?? 'open', has_super_admin: true, user: me }));
    return me;
  }, [qc]);

  const logout = useCallback(async () => {
    try { await auth.logout(); } finally {
      setCsrfToken('');
      sessionStorage.removeItem('active_portal_module');
      setOverrideModule(null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth-status' });
      await qc.invalidateQueries({ queryKey: ['auth-status'] });
    }
  }, [qc]);

  // Hidden unless the server (ENGINE_ENABLED) or the build (VITE_ENGINE_ENABLED=true) turns it on.
  const engineEnabled = status?.engine_enabled ?? (import.meta.env.VITE_ENGINE_ENABLED === 'true');
  const engineAllowed = engineEnabled && (!!user?.is_staff || (!user && status?.engine_access !== 'staff') || (!status && query.isError));

  const value = useMemo<AuthContextValue>(() => ({
    user,
    status,
    loading: query.isLoading,
    engineEnabled,
    engineAllowed,
    activeModule,
    setActiveModule,
    canSwitchModule,
    login,
    logout,
    refresh: () => query.refetch(),
  }), [user, status, query.isLoading, engineEnabled, engineAllowed, activeModule, setActiveModule, canSwitchModule, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}

export const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super admin', admin: 'Admin', analyst: 'Analyst', buyer: 'Buyer', seller: 'Seller',
};

export function isTradeAdmin(user: Me | null): boolean {
  return user?.role === 'super_admin' || user?.role === 'admin';
}

/** Where an account lands after sign-in: every role starts on its trade overview. */
export function homeFor(user: Me | null): string {
  return user ? '/trade' : '/welcome';
}
